import {
  Archive,
  EllipsisVertical,
  ListOrdered,
  LucideIcon,
  Pencil,
  RefreshCw,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { GetProjectIndexedPages200 } from "~/api/model/getProjectIndexedPages200";
import { useGetCurrentUser } from "~/api/users/users";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ArchiveCaseAlertDialog } from "~/features/cases/components/ArchiveCaseAlertDialog";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { EditCaseDialog } from "./EditCaseDialog";
import { IndexStatusBadge } from "./IndexStatusBadge";

interface CaseActionsProps {
  caseId: string;
  indexStatus: GetProjectIndexedPages200 | undefined;
  isReindexing: boolean;
  onReindex: () => void;
  fileCount: number;
}

interface DropdownItem {
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  onClick: () => void;
  disabled?: boolean;
}

export const CaseActions: FC<CaseActionsProps> = ({
  caseId,
  indexStatus,
  isReindexing,
  onReindex,
  fileCount,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: user } = useGetCurrentUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const handleArchived = () => {
    navigate(ROUTES.CASES({ organizationId }));
  };

  const isIndexed =
    indexStatus &&
    (indexStatus.index_status === "indexed" ||
      indexStatus.index_status === "completed");

  const isIndexing =
    indexStatus &&
    (indexStatus.index_status === "indexing" ||
      indexStatus.index_status === "pending");

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({ eventName: "app/case_actions.dropdown/open" });
    }
  }, [dropdownOpen]);

  const dropdownItems: DropdownItem[] = [
    {
      icon: Pencil,
      messageId: "edit",
      defaultMessage: "Edit",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/edit.link/click" });
        setShowEditDialog(true);
        setDropdownOpen(false);
      },
    },
    {
      icon: Archive,
      messageId: "archive",
      defaultMessage: "Archive",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/archive.link/click" });
        setShowArchiveDialog(true);
        setDropdownOpen(false);
      },
    },
  ];

  const handleIndexClick = () => {
    trackEvent({ eventName: "app/case_actions_menu/index.link/click" });
    onReindex();
    setDropdownOpen(false);
  };

  const isIndexDisabled = isReindexing || isIndexing;

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={intl.formatMessage({ id: "common.moreActions" })}
          >
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[160px] w-auto" align="start">
          {dropdownItems.map((item) => (
            <DropdownMenuItem
              key={item.messageId}
              onClick={item.disabled ? undefined : item.onClick}
              disabled={item.disabled}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <FormattedMessage
                id={item.messageId}
                defaultMessage={item.defaultMessage}
              />
            </DropdownMenuItem>
          ))}
          {user?.isAdminViewEnabled && fileCount > 0 && (
            <DropdownMenuItem
              onClick={isIndexDisabled ? undefined : handleIndexClick}
              disabled={isIndexDisabled}
              className="flex-col items-start"
            >
              <div className="flex items-center">
                {isIndexed ? (
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${isReindexing ? "animate-spin" : ""}`}
                  />
                ) : (
                  <ListOrdered className="mr-2 h-4 w-4" />
                )}
                <FormattedMessage
                  id={isIndexed ? "case.reindex" : "case.index"}
                  defaultMessage={isIndexed ? "Reindex" : "Index"}
                />
              </div>
              <div className="ml-6 mt-1 text-xs">
                <IndexStatusBadge status={indexStatus} hideIcon />
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {showEditDialog && (
        <EditCaseDialog
          open={showEditDialog}
          setOpen={setShowEditDialog}
          projectId={caseId}
        />
      )}

      {showArchiveDialog && (
        <ArchiveCaseAlertDialog
          open={showArchiveDialog}
          setOpen={setShowArchiveDialog}
          projectId={caseId}
          onArchived={handleArchived}
        />
      )}
    </>
  );
};
